type RentalType = "driving" | "chauffeured" | "event";

/** Hourly zero-distance service locations are still selected locations.
 * Do not treat an old null mode flag as consent to discard their address. */
export function hasPersistedTripDelivery(trip: {
  is_delivery?: unknown;
  airport_code?: unknown;
  delivery_distance?: unknown;
  rental_type?: unknown;
  delivery_address?: unknown;
} | null | undefined): boolean {
  if (!trip) return false;
  const distance = trip.delivery_distance == null ? NaN : Number(trip.delivery_distance);
  return trip.is_delivery === true || Boolean(trip.airport_code)
    || (Number.isFinite(distance) && distance > 0)
    || (["chauffeured", "event"].includes(String(trip.rental_type))
      && distance === 0 && Boolean(String(trip.delivery_address ?? "").trim()));
}

export interface RequestChangeLocationCapabilities {
  rentalType: RentalType;
  isOwner: boolean;
  isDelivery: boolean;
  carDeliveryEnabled: boolean;
  selfDriveDeliveryEnabled: boolean;
}

export interface RequestChangeLocationPolicy {
  deliveryEnabled: boolean;
  lockMode: boolean;
  allowPickupMode?: boolean;
  allowDeliveryMode?: boolean;
  requireDelivery: boolean;
}

/**
 * Keep the change-request mode rules directional. A self-drive booking that
 * already includes delivery can always be changed to pickup, even if the
 * listing's delivery preference has since been disabled. Adding delivery to a
 * pickup booking requires the dedicated Self-Drive delivery capability.
 */
export function getRequestChangeLocationPolicy({
  rentalType,
  isOwner,
  isDelivery,
  carDeliveryEnabled,
  selfDriveDeliveryEnabled,
}: RequestChangeLocationCapabilities): RequestChangeLocationPolicy {
  const deliveryCapability = rentalType === "driving"
    ? selfDriveDeliveryEnabled
    : carDeliveryEnabled;
  const deliveryEnabled = isDelivery || deliveryCapability;

  if (rentalType === "driving") {
    return {
      deliveryEnabled,
      lockMode: false,
      allowPickupMode: true,
      allowDeliveryMode: deliveryEnabled,
      requireDelivery: false,
    };
  }

  return {
    deliveryEnabled,
    // Preserve the existing policy for non-self-drive rentals: renters may
    // edit within their current mode, while hosts may switch unless an
    // existing delivery booking is no longer supported by the listing.
    lockMode: !isOwner || (isDelivery && !deliveryCapability),
    requireDelivery: rentalType === "event",
  };
}